import {createDataStore} from '@shared_web/lib/data_store';
import {notifyError} from '@shared_web/lib/notification';

import {apiCall} from '@mavismart_frontend/lib/api';

// Types

interface TrackingEventGeo {
  country?: string;
  region?: string;
  city?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}

export interface TrackingEvent {
  email: string;
  ts: number;
  action: string;
  route: string;
  ip: string;
  userAgent: string;
  metadata: Record<string, unknown>;
  geo?: TrackingEventGeo;
}

export interface TrackingEventsData {
  events: TrackingEvent[];
  nextPaginationToken?: string;
}

export interface TrackingStats {
  totalEvents: number;
  uniqueUsers: number;
  avgEventsPerDay: number;
  topAction: string;
  eventsByAction: Record<string, number>;
  eventsByDay: {day: string; count: number}[];
  topRegions: {name: string; count: number}[];
  topCities: {name: string; count: number}[];
}

// Stores

const trackingEventsStore = createDataStore<TrackingEventsData | undefined>(undefined);
const trackingStatsStore = createDataStore<TrackingStats | undefined>(undefined);

// Hooks

export const useTrackingEvents = trackingEventsStore.useData;
export const useTrackingStats = trackingStatsStore.useData;

// Actions

export const fetchTrackingEvents = async (params: {
  email?: string;
  action?: string;
  startTs?: number;
  endTs?: number;
  paginationToken?: string;
  limit: number;
}): Promise<TrackingEventsData | undefined> => {
  try {
    const response = await apiCall('POST /list-tracking-events', params);
    const data: TrackingEventsData = {
      events: response.events as TrackingEvent[],
      nextPaginationToken: response.nextPaginationToken,
    };
    trackingEventsStore.setData(data);
    return data;
  } catch (error) {
    notifyError(error);
    return undefined;
  }
};

export const fetchTrackingStats = async (startTs: number, endTs: number): Promise<void> => {
  try {
    const response = await apiCall('POST /get-tracking-stats', {startTs, endTs});
    trackingStatsStore.setData(response as TrackingStats);
  } catch (error) {
    notifyError(error);
  }
};

export const clearTrackingData = (): void => {
  trackingEventsStore.setData(undefined);
  trackingStatsStore.setData(undefined);
};
